"use client";

import React, {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import { useQueryClient } from "@tanstack/react-query";

// Define the type for network context
type NetworkContextType = {
  isOnline: boolean;
  isOffline: boolean;
  wasOffline: boolean;
  reconnectAttempts: number;
  forceRefetchAll: () => void;
};

// Create a new context
const NetworkContext = createContext<NetworkContextType | undefined>(undefined);

// Custom hook to use the network context
export const useNetwork = () => {
  const context = useContext(NetworkContext);
  if (!context) {
    throw new Error("useNetwork must be used within a NetworkProvider");
  }
  return context;
};

interface NetworkProviderProps {
  children: ReactNode;
}

// Network provider component
export const NetworkProvider: React.FC<NetworkProviderProps> = ({ children }) => {
  const [isOnline, setIsOnline] = useState(true);
  const [wasOffline, setWasOffline] = useState(false);
  const [reconnectAttempts, setReconnectAttempts] = useState(0);
  const queryClient = useQueryClient();

  const forceRefetchAll = useCallback(() => {
    console.log("Application is back online. Resuming and refetching active queries...");
    // Don't force reload the page, just refetch queries
    // Refetch all queries to ensure sessions and data are properly fetched
    queryClient.refetchQueries({
      type: "all",
      predicate: (query) => {
        // Refetch queries that were active or failed due to network issues
        return query.state.fetchStatus === "idle" || 
               query.state.error?.message?.includes("Network") ||
               query.queryKey.some(key => 
                 typeof key === 'string' && 
                 (key.includes('session') || key.includes('user') || key.includes('auth'))
               );
      }
    });
  }, [queryClient]);

  const handleOnline = useCallback(() => {
    setIsOnline(true);
    setReconnectAttempts(prev => prev + 1);
    
    if (wasOffline) {
      // Only refetch if we were previously offline
      forceRefetchAll();
      setWasOffline(false);
    }
  }, [wasOffline, forceRefetchAll]);

  const handleOffline = useCallback(() => {
    console.log("Application is offline.");
    setIsOnline(false);
    setWasOffline(true);
  }, []);

  useEffect(() => {
    // Set initial state
    setIsOnline(navigator.onLine);

    // Add event listeners
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    // Cleanup event listeners
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [handleOnline, handleOffline]);

  return (
    <NetworkContext.Provider
      value={{
        isOnline,
        isOffline: !isOnline,
        wasOffline,
        reconnectAttempts,
        forceRefetchAll,
      }}
    >
      {children}
    </NetworkContext.Provider>
  );
};